/**
 * Public Support page — required by App Store + Play Store listings.
 * Provides contact routes, business hours and a plain-English "what to
 * expect when you email us" panel.
 */
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Clock, LifeBuoy, ShieldCheck } from "lucide-react";

const FAQS = [
    {
        q: "I can't sign in.",
        a: "Enter your email at clientops.nz/login and we'll send a one-time code (or ask for your password if you have one). Codes expire in 10 minutes. Still stuck? Email us — we'll clear the block manually.",
    },
    {
        q: "Where is my data stored?",
        a: "Inside secure cloud infrastructure operated by Emergent (hosting) and MongoDB Atlas (database). Some AI features send extracts to Anthropic (Claude). Full detail lives on the privacy page.",
    },
    {
        q: "Can I get a copy of everything you have on me?",
        a: "Yes. Email privacy@clientops.nz. Under the NZ Privacy Act 2020 we'll respond within 20 working days.",
    },
    {
        q: "The app is stuck / a page won't load.",
        a: "First try pulling to refresh or closing and reopening. If it persists, email support@clientops.nz with the page you were on and roughly what time — we log everything and can trace it fast.",
    },
    {
        q: "How do I delete my account?",
        a: "Email support@clientops.nz from the address on the account. We remove personal data straight away; financial records we're legally required to keep are retained for the statutory 7 years, then deleted.",
    },
];

export default function Support() {
    return (
        <div className="min-h-screen bg-[#F5F4EF] text-[#0F1115]" data-testid="support-page">
            <header className="border-b border-[#E4E4E7] bg-white">
                <div className="mx-auto max-w-3xl px-6 py-5 flex items-center justify-between">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-xs text-[#5E636E] hover:text-[#0F1115]"
                        data-testid="support-back"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Back to ClientOps
                    </Link>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#5E636E]">Help</div>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-6 py-12">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#5E636E] mb-3">Support</div>
                <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                    We&apos;re a real accounting team — not a helpdesk queue.
                </h1>
                <p className="mt-4 text-sm leading-relaxed text-[#5E636E]">
                    Every question lands in front of a NZ Taxation Ltd staff member, not a bot. Most replies go out same working day.
                </p>

                {/* Contact cards */}
                <div className="mt-10 grid sm:grid-cols-2 gap-4">
                    <a
                        href="mailto:support@clientops.nz"
                        className="group rounded-2xl border border-[#E4E4E7] bg-white p-5 hover:border-[#0E3B2E] transition-colors"
                        data-testid="support-email-general"
                    >
                        <div className="grid h-10 w-10 place-items-center rounded-full bg-[#0E3B2E] text-white">
                            <LifeBuoy className="h-4.5 w-4.5" />
                        </div>
                        <div className="mt-3 text-[11px] uppercase tracking-[0.16em] text-[#5E636E]">General help</div>
                        <div className="mt-1 font-medium text-[15px]">support@clientops.nz</div>
                        <div className="mt-1 text-[12.5px] text-[#5E636E]">App issues, sign-in trouble, feature questions.</div>
                    </a>
                    <a
                        href="mailto:privacy@clientops.nz"
                        className="group rounded-2xl border border-[#E4E4E7] bg-white p-5 hover:border-[#0E3B2E] transition-colors"
                        data-testid="support-email-privacy"
                    >
                        <div className="grid h-10 w-10 place-items-center rounded-full bg-[#0E3B2E] text-white">
                            <ShieldCheck className="h-4.5 w-4.5" />
                        </div>
                        <div className="mt-3 text-[11px] uppercase tracking-[0.16em] text-[#5E636E]">Privacy &amp; data</div>
                        <div className="mt-1 font-medium text-[15px]">privacy@clientops.nz</div>
                        <div className="mt-1 text-[12.5px] text-[#5E636E]">Access, correction, deletion requests under the NZ Privacy Act 2020.</div>
                    </a>
                </div>

                {/* Hours */}
                <div className="mt-6 flex items-start gap-3 rounded-2xl border border-[#E4E4E7] bg-white p-5">
                    <div className="grid h-10 w-10 place-items-center rounded-full bg-emerald-50 text-emerald-800 shrink-0">
                        <Clock className="h-4.5 w-4.5" />
                    </div>
                    <div>
                        <div className="text-[13px] font-medium">Response times</div>
                        <div className="mt-1 text-[12.5px] text-[#5E636E] leading-relaxed">
                            Monday – Friday, 8:30 am – 5:00 pm NZT. Most tickets answered within one working day. Urgent tax-deadline issues jump the queue — flag &quot;deadline&quot; in the subject.
                        </div>
                    </div>
                </div>

                {/* FAQs */}
                <div className="mt-12">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#5E636E]">Common questions</div>
                    <h2 className="mt-1 font-display text-xl font-semibold">Before you email us</h2>
                    <div className="mt-5 space-y-4">
                        {FAQS.map((f, i) => (
                            <details key={i} className="group rounded-xl border border-[#E4E4E7] bg-white overflow-hidden" data-testid={`support-faq-${i}`}>
                                <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between hover:bg-[#FAFAFA]">
                                    <span className="text-[14px] font-medium pr-3">{f.q}</span>
                                    <span className="text-[#5E636E] group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                                </summary>
                                <div className="px-5 pb-5 text-[13px] leading-relaxed text-[#5E636E]">{f.a}</div>
                            </details>
                        ))}
                    </div>
                </div>

                {/* Fallback email */}
                <div className="mt-12 rounded-2xl bg-[#0E3B2E] text-white p-6">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#F0EDE5]/60">Still stuck?</div>
                    <h3 className="mt-1.5 font-display text-xl font-semibold">Tell us what happened.</h3>
                    <p className="mt-2 text-[13px] text-[#F0EDE5]/80 leading-relaxed">
                        The more specific, the faster we fix it — the page you were on, roughly what time, what you were trying to do, and any error text you saw.
                    </p>
                    <a
                        href="mailto:support@clientops.nz?subject=ClientOps%20help&body=What%20I%20was%20trying%20to%20do%3A%0A%0AWhat%20happened%20instead%3A%0A%0AApprox%20time%3A"
                        className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-[#0E3B2E] px-4 py-2 text-[13px] font-medium hover:bg-[#F5F4EF]"
                        data-testid="support-email-cta"
                    >
                        <Mail className="h-4 w-4" /> Email support@clientops.nz
                    </a>
                </div>
            </main>

            <footer className="border-t border-[#E4E4E7] bg-white">
                <div className="mx-auto max-w-3xl px-6 py-6 text-[11px] text-[#5E636E]">
                    ClientOps is operated by NZ Taxation Limited. See also our <Link to="/privacy" className="underline underline-offset-4">privacy policy</Link>.
                </div>
            </footer>
        </div>
    );
}
